import Echo from 'laravel-echo'
import {parseMessage} from '@/services/chatService'
import Pusher from 'pusher-js'

/**
 * THE PASSENGER'S REALTIME SOCKET — Laravel Reverb over pusher-js, for one thing only:
 * watching the driver move.
 *
 * ── Why this exists at all ────────────────────────────────────────────────────────
 * There is NO REST read path for a driver's position. The whole API carries exactly three
 * location routes and all three are POST: the driver submits, the passenger submits, and
 * the backend broadcasts. `TripResource.driver` carries a name, a phone and a vehicle and
 * no coordinates at all. So a passenger who wants to see the car has to hold a socket
 * open — polling has nothing to poll.
 *
 * ── ⚠️ WHY THIS IS NOT A COPY OF THE DRIVER PORTAL'S ──────────────────────────────
 * `hopn-driver-dashboard/src/services/reverb.js` is 20KB, and most of it is the ride-offer
 * flow: the personal notification channel, `.trip.actions.updated`, the catch-up sweep, a
 * telemetry reporter. A passenger has none of those concerns. What IS worth taking is the
 * connection contract and the tracing, and both are here.
 *
 * ── ⚠️ THE ONE FACT THAT MAKES DEBUGGING POSSIBLE ─────────────────────────────────
 * A private channel whose authorisation is REFUSED delivers nothing and throws nothing.
 * No error, no event, no exception — an app that silently shows an empty map looks
 * identical to one where the driver simply has not moved. That is why `traceSubscription`
 * and the raw frame log below are not debug scaffolding to be stripped: they are the only
 * way to tell "not authorised" from "nothing broadcast yet".
 *
 * Contract: `hopn-driver-dashboard/doc/realtime-mobile-client-guide.md`.
 */

const TAG = 'color:#00d4aa;font-weight:bold'
const log = (...args) => console.log('%c[reverb]', TAG, ...args)
const warn = (...args) => console.warn('%c[reverb]', TAG, ...args)

// Relative in dev (proxied — see vite.config.js), absolute in builds.
const API_BASE_URL = import.meta.env.DEV
    ? new URL(import.meta.env.VITE_API_BASE_URL).pathname
    : import.meta.env.VITE_API_BASE_URL
const API_KEY = import.meta.env.VITE_API_KEY

const REVERB = {
    key: import.meta.env.VITE_REVERB_APP_KEY,
    host: import.meta.env.VITE_REVERB_HOST,
    port: Number(import.meta.env.VITE_REVERB_PORT || 443),
    scheme: import.meta.env.VITE_REVERB_SCHEME || 'https',
}

/**
 * ⚠️ THE AUTH ENDPOINT IS THE REST BASE MINUS THE PORTAL PREFIX.
 *
 * Broadcasting auth is shared across every portal and lives at `/api/v1/broadcasting/auth`,
 * one level ABOVE `/api/v1/passengers`. Pointing it at the passenger base gives a 404 that
 * pusher-js reports as a generic subscription failure, which reads like an authorisation
 * problem and is not one.
 *
 * In dev it is RELATIVE, like the driver portal's: `/api` is proxied by the Vite dev
 * server (see vite.config.js). Builds have no proxy and use the absolute host.
 */
const AUTH_ENDPOINT = `${String(API_BASE_URL).replace(/\/passengers\/?$/, '')}/broadcasting/auth`

/**
 * ⚠️ EVERY EVENT IS BOUND UNDER MORE THAN ONE SPELLING.
 *
 * Laravel's `broadcastAs()` puts a leading dot on the wire name; without it the event
 * arrives as the fully-qualified class name. `.listen()` matches EXACTLY, so a binding
 * that guesses wrong is silent rather than wrong. Binding all three spellings costs
 * nothing and removes a whole class of "the backend says it sent it" investigation.
 */
const DRIVER_LOCATION_EVENTS = ['.driver.location.updated', '.DriverLocationUpdated', 'DriverLocationUpdated']
const TRIP_STATUS_EVENTS = ['.trip.status.updated', 'trip.status.updated']

/**
 * ⚠️ BOTH SPELLINGS, AND THIS IS NOT BELT AND BRACES. The leading dot is Laravel's
 * custom broadcast name and the backend has been observed sending both forms.
 * `channel.listen()` is an exact string match and fires nothing on a miss, warning about
 * nothing — so binding one form only is a feature that silently never works. This app's
 * sibling lost an entire release to exactly that on `passenger.location.updated`.
 */
const TRIP_MESSAGE_EVENTS = ['.trip.message.sent', 'trip.message.sent']

/**
 * A DELAY WAS GRANTED — the pickup time has moved.
 *
 * ⚠️ THE PAYLOAD IS NOT READ, AND THAT IS DELIBERATE. Neither mobile app parses it
 * either; its shape is undocumented and the driver's app looks only at `trip_id`. What
 * matters is that SOMETHING about this trip's delays changed, and the trip detail is the
 * only place the full truth lives. So this is a hint to refetch, never a source.
 */
const TRIP_DELAY_EVENTS = ['.trip.delay.requested', 'trip.delay.requested']

let echo = null
let tripChannelName = null
/* The user channel, and how many callers are holding the socket open — see `acquireReverb`. */
let userChannelName = null
let holders = 0
/** Removes the bfcache listeners — set when the socket is built, called on teardown. */
let lifecycleCleanup = null

/**
 * Module-level and replaced wholesale, never merged.
 *
 * ⚠️ SET OUTSIDE THE CONNECTION LIFECYCLE. The driver portal records the bug this avoids:
 * putting the handler assignment behind the "already connected" early return left the
 * callbacks undefined on a perfectly healthy socket, so events arrived and went nowhere.
 * The socket's identity and the handlers' identity are two different questions.
 */
let handlers = {}

/**
 * WHO WANTS TRIP MESSAGES — a set, because more than one screen can be listening and they
 * come and go independently of the socket.
 */
const messageListeners = new Set()
const delayListeners = new Set()
/* ⚠️ A SET FOR THE SAME REASON AS THE TWO ABOVE: `setReverbHandlers` REPLACES its whole
   object, so a notification handler registered there would be wiped the moment the
   tracking page registered its own. */
const notificationListeners = new Set()

export function setReverbHandlers(next) {
    handlers = next || {}
}

const bindAll = (channel, names, fn) => names.forEach((name) => channel.listen(name, fn))

/**
 * Say out loud whether a private channel was actually joined.
 *
 * The success line is as important as the failure one: "no car on the map" with a success
 * here means the driver has not pinged, which is a completely different problem from a
 * refused subscription and is not otherwise distinguishable.
 */
function traceSubscription(channel, name) {
    const pusherChannel = channel?.subscription
    pusherChannel?.bind('pusher:subscription_succeeded', () => log(`Channel Success ✓  ${name}`))
    pusherChannel?.bind('pusher:subscription_error', (payload) => {
        const status = payload?.status ?? payload?.error?.data?.code
        warn(`Channel Fail ✗  ${name}  · HTTP ${status ?? '?'}`)
        if (status === 403) warn('  403 — the API refused this passenger on this trip. Wrong trip, or the trip has ended.')
        if (status === 401) warn('  401 — the session token was rejected at /broadcasting/auth.')
        if (status === 404) warn(`  404 — no auth route at ${AUTH_ENDPOINT}.`)
    })
}

/** Whether the socket is up, for a UI that wants to say "reconnecting". */
export function isReverbConnected() {
    return echo?.connector?.pusher?.connection?.state === 'connected'
}

/**
 * Open the socket. Idempotent — a second call while connected is a no-op, so a component
 * remounting does not churn the connection.
 *
 * ⚠️ NO USER CHANNEL. The driver portal joins `App.Models.User.{id}` for ride offers and
 * warns that the id must be the USER id rather than the profile id, on pain of a silent
 * 403. A passenger tracking a trip needs only the trip channel, so that whole hazard is
 * simply not taken on.
 */
export function connectReverb({token}) {
    if (echo) return
    if (!token) return
    if (!REVERB.key || !REVERB.host) {
        warn('VITE_REVERB_APP_KEY / VITE_REVERB_HOST are not set — live tracking is off')
        return
    }

    const client = new Pusher(REVERB.key, {
        wsHost: REVERB.host,
        wsPort: REVERB.port,
        wssPort: REVERB.port,
        forceTLS: REVERB.scheme === 'https',
        enabledTransports: ['ws', 'wss'],
        // Required by the pusher-js constructor and unused by Reverb, which does no
        // cluster routing. Any value works; this is the one the mobile client sends.
        cluster: 'mt1',
        channelAuthorization: {
            endpoint: AUTH_ENDPOINT,
            transport: 'ajax',
            headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${token}`,
                'X-API-Key': API_KEY,
            },
        },
    })

    echo = new Echo({broadcaster: 'reverb', client})

    /* The CONNECTION's own state, which was never logged here — a socket Reverb refused
       (e.g. 4009 "Origin not allowed" for a dev origin) left only "connecting…" and a map
       waiting forever, with nothing saying why. */
    client.connection.bind('state_change', ({current}) => {
        if (current === 'connected') log('Connected ✓')
        else if (current === 'unavailable' || current === 'failed') warn(`Connection ${current} ✗`)
    })
    client.connection.bind('error', (err) => {
        const code = err?.error?.data?.code
        const message = err?.error?.data?.message || err?.error?.message || err?.type || 'unknown'
        warn(`connection error · ${code ?? ''} ${message}`)
        if (code === 4009) {
            warn(`  Reverb refused this ORIGIN (${window.location.origin}). Add it to the server's `
                + 'REVERB_ALLOWED_ORIGINS, or test on an allowed domain — no live driver location until then.')
        }
    })

    /*
     * ── ⚠️ THE BACK-FORWARD CACHE CLOSES THIS SOCKET, AND pusher-js READS IT AS A DROP
     *
     * Navigating away no longer destroys a page: the browser FREEZES it into the
     * back-forward cache so Back can restore it instantly. Open WebSockets do not survive
     * that, so the browser closes this one — with code 1006, which is indistinguishable
     * from the network falling over. pusher-js does what it should for a real drop and
     * starts retrying, from a frozen page, where nothing can succeed. The console fills
     * with `connection error · 1006` and a reconnect on every restore.
     *
     * ⚠️ THE NOISE IS THE LESSER HALF. This file exists partly to tell "not authorised"
     * apart from "nothing broadcast" — see the frame log below — and a loop of connection
     * errors buries exactly the line someone is looking for. Worse, the failed attempts
     * advance pusher-js's own backoff, so a passenger who presses Back can land on the
     * tracking map and watch it sit there before reconnecting, with no car arriving — and
     * there is no REST path to fall back on, because a driver's position exists only as a
     * broadcast.
     *
     * So the close is made DELIBERATE instead of discovered: disconnect on the way into
     * the cache, connect on the way out. pusher-js re-subscribes its own channels on
     * connect, so nothing here needs to rejoin them.
     *
     * ⚠️ `persisted` IS THE WHOLE TEST. It is false on a real unload, where the page is
     * going away and there is nothing to preserve; true only for the freeze.
     *
     * Mirrors the driver portal's copy — the two sockets have the same lifecycle problem.
     */
    const onPageHide = (event) => {
        if (!event.persisted) return
        try { client.disconnect() } catch { /* a half-open socket must never throw here */ }
    }
    const onPageShow = (event) => {
        if (!event.persisted) return
        try { client.connect() } catch { /* same */ }
    }
    window.addEventListener('pagehide', onPageHide)
    window.addEventListener('pageshow', onPageShow)
    lifecycleCleanup = () => {
        window.removeEventListener('pagehide', onPageHide)
        window.removeEventListener('pageshow', onPageShow)
    }

    /**
     * EVERY frame, printed BEFORE Echo gets a look at it.
     *
     * This is the line that answers "the backend says it broadcast — why did I see
     * nothing?". It prints under the channel and event name the SERVER used, so a name
     * drift shows up as a line that does not match anything bound above. Nothing here at
     * all means the frame never reached this browser.
     */
    echo.connector?.pusher?.connection?.bind('message', (frame) => {
        const event = frame?.event || ''
        if (event.startsWith('pusher:') || event.startsWith('pusher_internal:')) return
        /* ⚠️ EVERY FRAME, PRINTED BEFORE ECHO GETS A LOOK AT IT, and under the name the
           SERVER used. `.listen()` matches exactly and fires nothing on a miss, so a name
           that has drifted is silent at every other level — this is the only place it
           shows up, as a line that does not match the listening line above. */
        log('frame ·', frame?.channel, event)
    })

    log('connecting', `${REVERB.scheme}://${REVERB.host}:${REVERB.port}`)
}

/**
 * Join one trip's channel. ONE AT A TIME — the previous is left first, because a passenger
 * moving between trips otherwise accumulates channels that all still deliver, and the map
 * would take a position from whichever driver pinged last.
 */
export function subscribeTripChannel(tripId) {
    if (!echo || !tripId) return
    const name = `trip.${tripId}`
    if (tripChannelName === name) return
    unsubscribeTripChannel()

    const channel = echo.private(name)

    /*
     * ── THE ONE QUESTION THIS CONSOLE EXISTS TO ANSWER ──────────────────────
     *
     * "Is the driver's position arriving, and if not, how far does it get?" There are four
     * places it can stop, and each of them looks identical on the map — an empty one — so
     * each one says so in its own words:
     *
     *   1. the socket never connects       → Connected Fail
     *   2. the channel is not authorised   → Channel Fail, with the HTTP status
     *   3. nothing is broadcast            → the listening line, then silence
     *   4. the event name has drifted      → a raw frame logged whose name is not ours
     *
     * ⚠️ THE FIRST FIX IS ANNOUNCED, THE REST ARE NOT. Positions arrive every few seconds
     * for the whole ride; logging each one buries the line that matters under its own
     * success. What is worth knowing is the moment the answer changes from no to yes.
     */
    let announced = false
    bindAll(channel, DRIVER_LOCATION_EVENTS, (payload) => {
        if (!announced) {
            announced = true
            log(`driver location RECEIVED ✓ · ${name}`, payload)
        }
        handlers.onDriverLocation?.(payload)
    })
    bindAll(channel, TRIP_STATUS_EVENTS, (payload) => handlers.onTripStatus?.(payload))
    bindAll(channel, TRIP_MESSAGE_EVENTS, (payload) => {
        /* ⚠️ DELIVERED TO EVERY LISTENER, NOT TO ONE HANDLER. Chat is opened from more
           than one screen, and `setReverbHandlers` REPLACES its whole object — so a chat
           registering itself there would silently unregister the driver-position handler
           the map depends on. Subscribers keep their own list. */
        messageListeners.forEach((fn) => fn(payload))
    })
    bindAll(channel, TRIP_DELAY_EVENTS, (payload) => {
        delayListeners.forEach((fn) => fn(payload))
    })
    traceSubscription(channel, name)
    tripChannelName = name

    log(`listening on private-${name} for [${DRIVER_LOCATION_EVENTS.join(', ')}]`)
}

export function unsubscribeTripChannel() {
    if (echo && tripChannelName) echo.leave(tripChannelName)
    tripChannelName = null
}

export function disconnectReverb() {
    try { lifecycleCleanup?.() } catch { /* nothing depends on this succeeding */ }
    lifecycleCleanup = null

    unsubscribeTripChannel()
    unsubscribeUserChannel()
    echo?.disconnect?.()
    echo = null
    handlers = {}
}

/**
 * Listen for messages on one trip's thread. Returns an unsubscribe.
 *
 * ⚠️ THE PAYLOAD IS PARSED THROUGH SEVERAL PATHS, and a shape that does not parse is
 * DROPPED rather than guessed at. A dropped frame costs nothing here — the thread refetches
 * when the tab is next focused — where a half-understood one would render a broken row.
 *
 * ⚠️ AND IT IS FILTERED BY TRIP. One socket carries one trip channel at a time, but a
 * screen that outlived a channel switch would otherwise show another ride's messages.
 */
export function onTripMessage(tripId, handler) {
    const listener = (payload) => {
        const raw = payload?.message ?? payload?.data?.message ?? payload?.data ?? payload
        if (!raw || typeof raw !== 'object' || raw.id == null) return
        if (tripId && raw.trip_id != null && String(raw.trip_id) !== String(tripId)) return
        handler(parseMessage(raw))
    }
    messageListeners.add(listener)
    return () => messageListeners.delete(listener)
}

/**
 * Listen for delay changes on one trip. Returns an unsubscribe.
 *
 * ⚠️ THE HANDLER GETS NO DATA, ON PURPOSE. See `TRIP_DELAY_EVENTS`: the payload is
 * undocumented and unparsed by every client that receives it, so handing a caller fields
 * that might not exist would invite exactly the code this avoids. The event means "refetch
 * the trip", and that is all it is allowed to mean.
 *
 * ⚠️ AND IT IS FILTERED BY TRIP where the payload says which. One socket carries one
 * trip channel at a time, but a screen that outlived a channel switch would otherwise
 * refetch on another ride's delay.
 */
export function onTripDelay(tripId, handler) {
    const listener = (payload) => {
        const eventTripId = payload?.trip_id ?? payload?.data?.trip_id
        if (tripId && eventTripId != null && String(eventTripId) !== String(tripId)) return
        handler()
    }
    delayListeners.add(listener)
    return () => delayListeners.delete(listener)
}


/**
 * ── SHARED OWNERSHIP OF THE SOCKET ─────────────────────────────────────────────────
 *
 * ⚠️ THE SOCKET HAS TWO OWNERS NOW, and a plain connect/disconnect pair cannot serve both.
 * It used to belong to the tracking page alone — `useDriverPosition` opened it on mount and
 * closed the whole thing on unmount, with a note to revisit if anything else ever needed
 * Reverb. The passenger's user channel does: it has to be joined on EVERY page, because a
 * flight-shifted pickup can arrive while they are anywhere in the dashboard. Left as it
 * was, leaving the tracking page would disconnect the app-wide listener with it.
 *
 * So callers ACQUIRE and RELEASE. The socket opens with the first holder and closes with
 * the last, and nobody's unmount can pull it out from under somebody else.
 */
export function acquireReverb({token}) {
    holders += 1
    connectReverb({token})
}

export function releaseReverb() {
    holders = Math.max(0, holders - 1)
    if (holders === 0) disconnectReverb()
}

const NOTIFICATION_EVENTS = ['.notification.broadcast', 'notification.broadcast']

/**
 * Join `App.Models.User.{id}` — the passenger's own notification channel.
 *
 * ⚠️ THE USER ID, NOT A PASSENGER PROFILE ID. The channel is authorised against the
 * authenticated USER, and handing it any other id is a silent 403: the subscription fails,
 * nothing is broadcast to it, and there is no error anywhere but the trace below. It comes
 * from `/account`'s `user.id`.
 */
export function subscribeUserChannel(userId) {
    if (!echo || !userId) return
    const name = `App.Models.User.${userId}`
    if (userChannelName === name) return
    unsubscribeUserChannel()

    const channel = echo.private(name)
    bindAll(channel, NOTIFICATION_EVENTS, (payload) => {
        notificationListeners.forEach((fn) => fn(payload))
    })
    traceSubscription(channel, name)
    userChannelName = name
    log(`listening on private-${name} for [${NOTIFICATION_EVENTS.join(', ')}]`)
}

export function unsubscribeUserChannel() {
    if (echo && userChannelName) echo.leave(userChannelName)
    userChannelName = null
}

/** Listen for user-channel notifications. Returns an unsubscribe. */
export function onUserNotification(handler) {
    notificationListeners.add(handler)
    return () => notificationListeners.delete(handler)
}
